<template>
  <div id="layout-wrapper" :class="{ 'nav-collapsed': isNavCollapsed }">
    <NavigationBar @nav-toggle="handleNavToggle" />
    <div class="content-minimize">
      <div class="page-content">
        <div class="page-inner">
          <!-- Page Header -->
          <div class="row">
            <div class="col-12">
              <div class="page-title-box d-sm-flex align-items-center justify-content-between">
                <h4 class="mb-sm-0 font-size-18">Employee Details</h4>
                <div class="page-title-right">
                  <ol class="breadcrumb m-0">
                    <li class="breadcrumb-item"><a href="javascript:void(0)" @click="gotTo('/')">Home</a></li>
                    <li class="breadcrumb-item"><a href="javascript:void(0)" @click="gotTo('employee-register')">Employees</a></li>
                    <li class="breadcrumb-item active">Employee Details</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>

          <div v-if="loading" class="text-center p-5">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
            <p class="mt-2">Loading employee details...</p>
          </div>

          <div v-else-if="employee" class="row">
            <!-- Employee Summary Card -->
            <div class="col-12 mb-4">
              <div class="card">
                <div class="card-body">
                  <div class="row align-items-center">
                    <div class="col-md-2 text-center">
                      <div class="avatar-xl mx-auto mb-3 mb-md-0">
                        <span class="avatar-title rounded-circle bg-primary bg-gradient text-white font-size-24">
                          {{ getInitials(employee.firstName, employee.lastName) }}
                        </span>
                      </div>
                    </div>
                    <div class="col-md-10">
                      <div class="row align-items-start">
                        <div class="col-md-6">
                          <div class="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-1">
                            <h4 class="mb-0">{{ getFullName() }}</h4>
                            <button type="button" class="btn btn-sm btn-outline-secondary sensitive-toggle-btn" @click="sensitiveRevealed = !sensitiveRevealed">
                              <i class="mdi" :class="sensitiveRevealed ? 'mdi-eye-off' : 'mdi-eye'"></i>
                              {{ sensitiveRevealed ? 'Hide sensitive fields' : 'Reveal sensitive fields' }}
                            </button>
                          </div>
                          <p class="text-muted mb-1">{{ employee.empNo || 'N/A' }}</p>
                          <div class="d-flex flex-wrap gap-2 mb-2">
                            <span :class="getStatusClass(employee.status)">{{ employee.status || 'N/A' }}</span>
                            <span :class="getEmploymentTypeClass(employee.employmentType)">
                              {{ displayEmploymentType(employee.employmentType) }}
                            </span>
                          </div>
                        </div>
                        <div class="col-md-6">
                          <div class="row text-muted">
                            <div class="col-sm-6">
                              <p class="mb-1"><i class="mdi mdi-office-building me-1"></i> {{ employee.departmentName || 'N/A' }}</p>
                              <p class="mb-1"><i class="mdi mdi-briefcase-outline me-1"></i> {{ employee.positionTitle || employee.designation || 'N/A' }}</p>
                              <p class="mb-1"><i class="mdi mdi-email me-1"></i> {{ displayEmail(employee.email) }}</p>
                              <p v-if="employee.payGroup" class="mb-1"><i class="mdi mdi-account-group me-1"></i> Pay group: {{ displayPayGroup(employee.payGroup) }}</p>
                            </div>
                            <div class="col-sm-6">
                              <p class="mb-1"><i class="mdi mdi-phone me-1"></i> {{ displayPhone(employee.phone) }}</p>
                              <p class="mb-1"><i class="mdi mdi-calendar me-1"></i> Joined: {{ formatDate(employee.hireDate) }}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Navigation Tabs -->
            <div class="col-12">
              <div class="card">
                <div class="card-header">
                  <ul class="nav nav-tabs card-header-tabs" role="tablist">
                    <li class="nav-item">
                      <a class="nav-link" :class="{ active: activeTab === 'personal' }"
                         @click="activeTab = 'personal'" href="javascript:void(0)">
                        <i class="mdi mdi-account me-1"></i> Personal Info
                      </a>
                    </li>
                    <li class="nav-item">
                      <a class="nav-link" :class="{ active: activeTab === 'governmentIds' }"
                         @click="activeTab = 'governmentIds'" href="javascript:void(0)">
                        <i class="mdi mdi-card-account-details me-1"></i>
                        Government IDs <span class="badge bg-primary ms-1">{{ governmentIds.length }}</span>
                      </a>
                    </li>
                    <li class="nav-item">
                      <a class="nav-link" :class="{ active: activeTab === 'documents' }"
                         @click="activeTab = 'documents'" href="javascript:void(0)">
                        <i class="mdi mdi-file-document-multiple me-1"></i>
                        Documents <span class="badge bg-primary ms-1">{{ documents.length }}</span>
                      </a>
                    </li>
                    <li class="nav-item">
                      <a class="nav-link" :class="{ active: activeTab === 'contacts' }"
                         @click="activeTab = 'contacts'" href="javascript:void(0)">
                        <i class="mdi mdi-phone-alert me-1"></i>
                        Emergency Contacts <span class="badge bg-warning ms-1">{{ emergencyContacts.length }}</span>
                      </a>
                    </li>
                    <li class="nav-item">
                      <a class="nav-link" :class="{ active: activeTab === 'kin' }"
                         @click="activeTab = 'kin'" href="javascript:void(0)">
                        <i class="mdi mdi-account-heart me-1"></i>
                        Next of Kin <span class="badge bg-info ms-1">{{ nextOfKin.length }}</span>
                      </a>
                    </li>
                  </ul>
                </div>

                <div class="card-body">
                  <!-- Personal Information Tab -->
                  <div v-show="activeTab === 'personal'" class="tab-pane">
                    <div class="row">
                      <div class="col-md-6">
                        <div class="table-responsive">
                          <table class="table table-borderless">
                            <tbody>
                            <tr>
                              <td class="fw-semibold text-muted" style="width: 40%;">Employee Number</td>
                              <td>{{ employee.empNo || 'N/A' }}</td>
                            </tr>
                            <tr>
                              <td class="fw-semibold text-muted">Title</td>
                              <td>{{ employee.title || employee.salutation || 'N/A' }}</td>
                            </tr>
                            <tr>
                              <td class="fw-semibold text-muted">First Name</td>
                              <td>{{ employee.firstName || 'N/A' }}</td>
                            </tr>
                            <tr>
                              <td class="fw-semibold text-muted">Middle Name</td>
                              <td>{{ employee.middleName || 'N/A' }}</td>
                            </tr>
                            <tr>
                              <td class="fw-semibold text-muted">Last Name</td>
                              <td>{{ employee.lastName || 'N/A' }}</td>
                            </tr>
                            <tr>
                              <td class="fw-semibold text-muted">Email</td>
                              <td>
                                <template v-if="sensitiveRevealed"><a :href="'mailto:' + employee.email">{{ employee.email || 'N/A' }}</a></template>
                                <template v-else>{{ displayEmail(employee.email) }}</template>
                              </td>
                            </tr>
                            <tr>
                              <td class="fw-semibold text-muted">Phone</td>
                              <td>
                                <template v-if="sensitiveRevealed"><a :href="'tel:' + employee.phone">{{ employee.phone || 'N/A' }}</a></template>
                                <template v-else>{{ displayPhone(employee.phone) }}</template>
                              </td>
                            </tr>
                            <tr>
                              <td class="fw-semibold text-muted">National ID</td>
                              <td>{{ employee.nationalId || 'N/A' }}</td>
                            </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                      <div class="col-md-6">
                        <div class="table-responsive">
                          <table class="table table-borderless">
                            <tbody>
                            <tr>
                              <td class="fw-semibold text-muted" style="width: 40%;">Gender</td>
                              <td>{{ employee.gender || 'N/A' }}</td>
                            </tr>
                            <tr>
                              <td class="fw-semibold text-muted">Date of Birth</td>
                              <td>{{ formatDate(employee.dateOfBirth) }}</td>
                            </tr>
                            <tr>
                              <td class="fw-semibold text-muted">Nationality</td>
                              <td>{{ employee.nationality || 'N/A' }}</td>
                            </tr>
                            <tr>
                              <td class="fw-semibold text-muted">Department</td>
                              <td>{{ employee.departmentName || 'N/A' }}</td>
                            </tr>
                            <tr>
                              <td class="fw-semibold text-muted">Position / Designation</td>
                              <td>{{ employee.positionTitle || employee.designation || 'N/A' }}</td>
                            </tr>
                            <tr>
                              <td class="fw-semibold text-muted">Employment Type</td>
                              <td>
                                  <span :class="getEmploymentTypeClass(employee.employmentType)">
                                    {{ employee.employmentType || 'N/A' }}
                                  </span>
                              </td>
                            </tr>
                            <tr>
                              <td class="fw-semibold text-muted">Hire Date</td>
                              <td>{{ formatDate(employee.hireDate) }}</td>
                            </tr>
                            <tr>
                              <td class="fw-semibold text-muted">Status</td>
                              <td>
                                  <span :class="getStatusClass(employee.status)">
                                    {{ employee.status || 'N/A' }}
                                  </span>
                              </td>
                            </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Government IDs Tab -->
                  <div v-show="activeTab === 'governmentIds'" class="tab-pane">
                    <div v-if="governmentIds.length === 0" class="text-center py-5">
                      <div class="avatar-lg mx-auto mb-4">
                        <div class="avatar-title bg-light text-muted rounded-circle">
                          <i class="mdi mdi-card-account-details-outline font-size-24"></i>
                        </div>
                      </div>
                      <h5 class="mt-4">No government IDs available</h5>
                      <p class="text-muted">This employee has no government identification records on file</p>
                    </div>

                    <div v-else class="row">
                      <div v-for="(govId, index) in governmentIds" :key="index" class="col-md-6 col-lg-4 mb-3">
                        <div class="card h-100 border">
                          <div class="card-body">
                            <div class="d-flex justify-content-between align-items-start mb-3">
                              <div class="document-icon">
                                <i class="mdi mdi-card-account-details font-size-24 text-primary"></i>
                              </div>
                              <span :class="getIdTypeClass(govId.id_type)">{{ formatIdType(govId.id_type) }}</span>
                            </div>

                            <h6 class="card-title mb-2">{{ formatIdType(govId.id_type) }}</h6>

                            <div class="document-details">
                              <p class="text-muted mb-1">
                                <i class="mdi mdi-identifier me-1"></i>
                                <strong>Number:</strong> {{ govId.id_number || 'N/A' }}
                              </p>
                              <p class="text-muted mb-1">
                                <i class="mdi mdi-flag me-1"></i>
                                <strong>Country:</strong> {{ govId.country_code || 'N/A' }}
                              </p>
                              <p v-if="govId.issue_date" class="text-muted mb-1">
                                <i class="mdi mdi-calendar-check me-1"></i>
                                <strong>Issue Date:</strong> {{ formatDate(govId.issue_date) }}
                              </p>
                              <p v-if="govId.expiry_date" class="text-muted mb-1">
                                <i class="mdi mdi-calendar-clock me-1"></i>
                                <strong>Expiry Date:</strong> {{ formatDate(govId.expiry_date) }}
                                <span v-if="isExpired(govId.expiry_date)" class="badge bg-danger ms-1">
                                  <i class="mdi mdi-alert-circle-outline"></i> Expired
                                </span>
                                <span v-else-if="isExpiringSoon(govId.expiry_date)" class="badge bg-warning ms-1">
                                  <i class="mdi mdi-clock-alert-outline"></i> Expiring Soon
                                </span>
                              </p>
                              <p v-if="govId.notes" class="text-muted mb-1 small">
                                <i class="mdi mdi-note-text me-1"></i>
                                {{ govId.notes }}
                              </p>
                            </div>

                            <div class="mt-3">
                              <div class="d-flex justify-content-between align-items-center">
                                <div class="verification-status">
                                  <span v-if="govId.is_verified === '1'" class="badge bg-success">
                                    <i class="mdi mdi-check-circle me-1"></i>Verified
                                  </span>
                                  <span v-else class="badge bg-warning">
                                    <i class="mdi mdi-clock me-1"></i>Unverified
                                  </span>
                                  <span v-if="govId.is_active === '1'" class="badge bg-info ms-1">
                                    <i class="mdi mdi-check"></i>Active
                                  </span>
                                  <span v-else class="badge bg-secondary ms-1">
                                    <i class="mdi mdi-close"></i>Inactive
                                  </span>
                                </div>
                              </div>
                              <div v-if="govId.verified_by" class="mt-2">
                                <small class="text-muted">
                                  Verified by: {{ govId.verified_by }} on {{ formatDate(govId.verification_date) }}
                                </small>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Documents Tab -->
                  <div v-show="activeTab === 'documents'" class="tab-pane">
                    <div class="d-flex justify-content-end mb-3">
                      <button type="button" class="btn btn-primary btn-sm" @click="openUploadModal">
                        <i class="mdi mdi-upload me-1"></i> Upload document for this employee
                      </button>
                    </div>
                    <div v-if="documents.length === 0" class="text-center py-5">
                      <div class="avatar-lg mx-auto mb-4">
                        <div class="avatar-title bg-light text-muted rounded-circle">
                          <i class="mdi mdi-file-document-outline font-size-24"></i>
                        </div>
                      </div>
                      <h5 class="mt-4">No documents available</h5>
                      <p class="text-muted">This employee has no documents on file</p>
                    </div>

                    <div v-else class="row">
                      <div v-for="(doc, index) in documents" :key="index" class="col-md-6 col-lg-4 mb-3">
                        <div class="card h-100 border">
                          <div class="card-body">
                            <div class="d-flex justify-content-between align-items-start mb-3">
                              <div class="document-icon">
                                <i class="mdi mdi-file-document-outline font-size-24 text-primary"></i>
                              </div>
                              <span class="badge bg-secondary">{{ doc.documentType }}</span>
                            </div>

                            <h6 class="card-title mb-2">{{ doc.name || 'Untitled Document' }}</h6>

                            <div class="document-details">
                              <p class="text-muted mb-1 small">
                                <i class="mdi mdi-identifier me-1"></i>
                                Number: {{ doc.documentNumber || 'N/A' }}
                              </p>
                              <p class="text-muted mb-1 small">
                                <i class="mdi mdi-calendar-upload me-1"></i>
                                Uploaded: {{ formatDate(doc.uploadDate) }}
                              </p>
                              <p v-if="doc.validTill" class="text-muted mb-1 small">
                                <i class="mdi mdi-calendar-clock me-1"></i>
                                Valid until: {{ formatDate(doc.validTill) }}
                              </p>
                              <p v-if="doc.fileSize" class="text-muted mb-1 small">
                                <i class="mdi mdi-file-outline me-1"></i>
                                Size: {{ formatFileSize(doc.fileSize) }}
                              </p>
                            </div>

                            <div class="mt-3">
                              <div class="d-flex justify-content-between align-items-center">
                                <div class="verification-status">
                                  <span v-if="doc.isVerified === '1'" class="badge bg-success">
                                    <i class="mdi mdi-check-circle me-1"></i>Verified
                                  </span>
                                  <span v-else class="badge bg-warning">
                                    <i class="mdi mdi-clock me-1"></i>Pending
                                  </span>
                                </div>
                                <div class="btn-group btn-group-sm">
                                  <button v-if="doc.filePath" @click="viewDocument(doc)"
                                          class="btn btn-outline-primary btn-sm" title="View Document">
                                    <i class="mdi mdi-eye"></i>
                                  </button>
                                  <button class="btn btn-outline-info btn-sm" title="Download">
                                    <i class="mdi mdi-download"></i>
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Emergency Contacts Tab -->
                  <div v-show="activeTab === 'contacts'" class="tab-pane">
                    <div v-if="emergencyContacts.length === 0" class="text-center py-5">
                      <div class="avatar-lg mx-auto mb-4">
                        <div class="avatar-title bg-light text-muted rounded-circle">
                          <i class="mdi mdi-phone-alert font-size-24"></i>
                        </div>
                      </div>
                      <h5 class="mt-4">No emergency contacts</h5>
                      <p class="text-muted">No emergency contacts have been added for this employee</p>
                    </div>

                    <div v-else class="row">
                      <div v-for="(contact, index) in emergencyContacts" :key="index" class="col-md-6 col-lg-4 mb-3">
                        <div class="card h-100 border">
                          <div class="card-body">
                            <div class="d-flex justify-content-between align-items-start mb-3">
                              <div class="contact-icon">
                                <i class="mdi mdi-account-circle font-size-24 text-warning"></i>
                              </div>
                              <span class="badge bg-warning">Emergency Contact</span>
                            </div>

                            <h6 class="card-title mb-2">{{ contact.name || 'N/A' }}</h6>

                            <div class="contact-details">
                              <p class="text-muted mb-1">
                                <i class="mdi mdi-phone me-1"></i>
                                <a :href="'tel:' + contact.phoneNumber">{{ contact.phoneNumber || 'N/A' }}</a>
                              </p>
                              <p v-if="contact.email" class="text-muted mb-1">
                                <i class="mdi mdi-email me-1"></i>
                                <a :href="'mailto:' + contact.email">{{ contact.email }}</a>
                              </p>
                              <p class="text-muted mb-1">
                                <i class="mdi mdi-account-heart me-1"></i>
                                Relationship: {{ contact.relationship || 'N/A' }}
                              </p>
                            </div>

                            <div class="mt-3">
                              <div class="btn-group btn-group-sm w-100">
                                <button @click="callContact(contact.phoneNumber)" class="btn btn-outline-success btn-sm">
                                  <i class="mdi mdi-phone me-1"></i>Call
                                </button>
                                <button v-if="contact.email" @click="emailContact(contact.email)" class="btn btn-outline-primary btn-sm">
                                  <i class="mdi mdi-email me-1"></i>Email
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Next of Kin Tab -->
                  <div v-show="activeTab === 'kin'" class="tab-pane">
                    <div v-if="nextOfKin.length === 0" class="text-center py-5">
                      <div class="avatar-lg mx-auto mb-4">
                        <div class="avatar-title bg-light text-muted rounded-circle">
                          <i class="mdi mdi-account-heart font-size-24"></i>
                        </div>
                      </div>
                      <h5 class="mt-4">No next of kin records</h5>
                      <p class="text-muted">No next of kin information has been added for this employee</p>
                    </div>

                    <div v-else class="row">
                      <div v-for="(kin, index) in nextOfKin" :key="index" class="col-md-6 col-lg-4 mb-3">
                        <div class="card h-100 border">
                          <div class="card-body">
                            <div class="d-flex justify-content-between align-items-start mb-3">
                              <div class="kin-icon">
                                <i class="mdi mdi-account-heart font-size-24 text-info"></i>
                              </div>
                              <span class="badge bg-info">Next of Kin</span>
                            </div>

                            <h6 class="card-title mb-2">{{ kin.name || 'N/A' }}</h6>

                            <div class="kin-details">
                              <p class="text-muted mb-1">
                                <i class="mdi mdi-phone me-1"></i>
                                <a :href="'tel:' + kin.phoneNumber">{{ kin.phoneNumber || 'N/A' }}</a>
                              </p>
                              <p v-if="kin.email" class="text-muted mb-1">
                                <i class="mdi mdi-email me-1"></i>
                                <a :href="'mailto:' + kin.email">{{ kin.email }}</a>
                              </p>
                              <p class="text-muted mb-1">
                                <i class="mdi mdi-account-heart me-1"></i>
                                Relationship: {{ kin.relationship || 'N/A' }}
                              </p>
                              <p class="mb-1">
                                <span v-if="kin.isCoveredInBenefits === '1'" class="badge bg-success">
                                  <i class="mdi mdi-check-circle me-1"></i>Covered in Benefits
                                </span>
                                <span v-else class="badge bg-outline-secondary">
                                  <i class="mdi mdi-close-circle me-1"></i>Not Covered
                                </span>
                              </p>
                            </div>

                            <div class="mt-3">
                              <div class="btn-group btn-group-sm w-100">
                                <button @click="callContact(kin.phoneNumber)" class="btn btn-outline-success btn-sm">
                                  <i class="mdi mdi-phone me-1"></i>Call
                                </button>
                                <button v-if="kin.email" @click="emailContact(kin.email)" class="btn btn-outline-primary btn-sm">
                                  <i class="mdi mdi-email me-1"></i>Email
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Action Buttons -->
            <div class="col-12">
              <div class="card">
                <div class="card-body">
                  <div class="d-flex justify-content-between">
                    <button @click="gotTo('employee-register')" type="button" class="btn btn-outline-secondary">
                      <i class="mdi mdi-arrow-left me-2"></i>
                      Back to List
                    </button>
                    <div>
                      <button v-if="canEditEmployees" @click="editEmployee" type="button" class="btn btn-primary me-2">
                        <i class="mdi mdi-pencil me-2"></i>
                        Edit Employee
                      </button>
                      <div v-if="canEditEmployees" class="btn-group">
                        <button type="button" class="btn btn-info dropdown-toggle" data-bs-toggle="dropdown">
                          <i class="mdi mdi-dots-vertical me-1"></i>
                          Actions
                        </button>
                        <ul class="dropdown-menu">
                          <li><a class="dropdown-item" href="javascript:void(0)">
                            <i class="mdi mdi-file-document-outline me-2"></i>Generate Report
                          </a></li>
                          <li><a class="dropdown-item" href="javascript:void(0)">
                            <i class="mdi mdi-email-send me-2"></i>Send Email
                          </a></li>
                          <li><a class="dropdown-item" href="javascript:void(0)">
                            <i class="mdi mdi-printer me-2"></i>Print Details
                          </a></li>
                          <li><hr class="dropdown-divider"></li>
                          <li><a class="dropdown-item text-danger" href="javascript:void(0)">
                            <i class="mdi mdi-delete me-2"></i>Delete Employee
                          </a></li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Upload document on behalf modal -->
    <div v-if="showUploadModal" class="modal d-block" tabindex="-1" style="background: rgba(0,0,0,0.5);">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Upload document for {{ getFullName() }}</h5>
            <button type="button" class="btn-close" @click="closeUploadModal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <form @submit.prevent="submitUploadOnBehalf">
              <div class="mb-3">
                <label class="form-label">Document type <span class="text-danger">*</span></label>
                <select v-model="uploadForm.documentType" class="form-select" required>
                  <option value="">Select type</option>
                  <option value="Academic Certificate">Academic Certificate</option>
                  <option value="Professional Certificate">Professional Certificate</option>
                  <option value="CV/Resume">CV/Resume</option>
                  <option value="ID">National ID</option>
                  <option value="Passport">Passport</option>
                  <option value="Contract">Employment Contract</option>
                  <option value="Letter">Letter</option>
                  <option value="Medical">Medical Document</option>
                  <option value="Tax">Tax Document</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div class="mb-3">
                <label class="form-label">Document name <span class="text-danger">*</span></label>
                <input v-model="uploadForm.documentName" type="text" class="form-control" placeholder="E.g. Degree certificate" required>
              </div>
              <div class="mb-3">
                <label class="form-label">Document number (optional)</label>
                <input v-model="uploadForm.documentNumber" type="text" class="form-control" placeholder="E.g. #12345">
              </div>
              <div class="mb-3">
                <label class="form-label">Valid until (optional)</label>
                <input v-model="uploadForm.validTill" type="date" class="form-control">
              </div>
              <div class="mb-3">
                <label class="form-label">File <span class="text-danger">*</span></label>
                <input type="file" class="form-control" ref="uploadFileInput" @change="handleUploadFileSelect"
                       accept=".pdf,.jpg,.jpeg,.png,.doc,.docx">
                <small class="text-muted">PDF, JPG, PNG, DOC, DOCX. Max 5MB. Files are compressed when possible.</small>
              </div>
              <div class="modal-footer">
                <button type="button" class="btn btn-secondary" @click="closeUploadModal">Cancel</button>
                <button type="submit" class="btn btn-primary" :disabled="uploading || !uploadForm.file">
                  <span v-if="uploading" class="spinner-border spinner-border-sm me-1"></span>
                  {{ uploading ? 'Uploading...' : 'Upload' }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import NavigationBar from "@/components/Navigation";
import {authUrl, base, execute, uploads, getRight} from "@/api";
import { maskEmail, maskPhone, maskPlain } from "@/utils/sensitiveEmployeeFields";

export default {
  name: "EmployeeDetailsView",
  components: { NavigationBar },
  data() {
    return {
      isNavCollapsed: false,
      loading: false,
      sensitiveRevealed: false,
      activeTab: 'personal',
      employee: null,
      governmentIds: [],
      documents: [],
      emergencyContacts: [],
      nextOfKin: [],
      showUploadModal: false,
      uploading: false,
      uploadForm: {
        documentType: '',
        documentName: '',
        documentNumber: '',
        validTill: '',
        file: null
      }
    };
  },

  computed: {
    canEditEmployees() {
      return getRight(["EMPLOYEE REGISTER", "DIRECTOR HR", "DIRECTOR OF HR"]);
    }
  },

  mounted() {
    this.loadEmployeeDetails();
  },

  methods: {
    displayEmail(val) {
      if (this.sensitiveRevealed) return val || "N/A";
      return maskEmail(val);
    },
    displayPhone(val) {
      if (this.sensitiveRevealed) return val || "N/A";
      return maskPhone(val);
    },
    displayPayGroup(val) {
      if (this.sensitiveRevealed) return val || "N/A";
      return maskPlain(val);
    },
    displayEmploymentType(val) {
      if (this.sensitiveRevealed) return val || "N/A";
      const s = String(val || "").trim();
      return s ? maskPlain(s) : "N/A";
    },

    handleNavToggle(isCollapsed) {
      this.isNavCollapsed = isCollapsed;
    },
    async loadEmployeeDetails() {
      this.loading = true;

      try {
        const data = new FormData();
        data.append("function", "getEmployeeWithDetails");
        data.append("employeeId", this.$route.params.id || sessionStorage.getItem("employeeId"));

        const res = await execute(data, authUrl);

        if (res && res.data && res.data.success) {
          const empData = res.data.data;

          this.employee = empData.employee;
          this.governmentIds = empData.governmentIds || [];
          this.documents = empData.documents || [];
          this.emergencyContacts = empData.emergencyContacts || [];
          this.nextOfKin = empData.nextOfKin || [];
        } else {
          throw new Error(res.data?.message || 'Failed to load employee details');
        }
      } catch (error) {
        this.$toast?.error("Failed to load employee details: " + error.message) ||
        console.error("Failed to load employee details:", error);
      } finally {
        this.loading = false;
      }
    },

    getInitials(firstName, lastName) {
      return ((firstName ? firstName.charAt(0) : '') + (lastName ? lastName.charAt(0) : '')).toUpperCase();
    },

    getFullName() {
      const title = this.employee.title || this.employee.salutation || '';
      const parts = [title, this.employee.firstName, this.employee.middleName, this.employee.lastName].filter(Boolean);
      return parts.join(' ') || 'N/A';
    },

    getStatusClass(status) {
      const statusLower = status?.toLowerCase();
      if (statusLower === 'active') return 'badge bg-success';
      if (statusLower === 'resigned' || statusLower === 'terminated') return 'badge bg-danger';
      if (statusLower === 'suspended') return 'badge bg-warning';
      return 'badge bg-secondary';
    },

    getEmploymentTypeClass(type) {
      const classes = {
        'Permanent': 'badge bg-success',
        'Contract': 'badge bg-info',
        'Temporary': 'badge bg-warning',
        'Intern': 'badge bg-primary',
        'Consultant': 'badge bg-secondary'
      };
      return classes[type] || 'badge bg-secondary';
    },

    formatIdType(idType) {
      const idTypeNames = {
        'NATIONAL_ID': 'National ID',
        'KRA_PIN': 'KRA PIN',
        'NHIF': 'NHIF Number',
        'NSSF': 'NSSF Number',
        'SHIF': 'SHIF Number',
        'PASSPORT': 'Passport',
        'DRIVING_LICENSE': 'Driving License',
        'BIRTH_CERT': 'Birth Certificate'
      };
      return idTypeNames[idType] || idType?.replace(/_/g, ' ') || 'Unknown';
    },

    getIdTypeClass(idType) {
      const classes = {
        'NATIONAL_ID': 'badge bg-primary',
        'KRA_PIN': 'badge bg-success',
        'NHIF': 'badge bg-info',
        'NSSF': 'badge bg-warning',
        'SHIF': 'badge bg-secondary',
        'PASSPORT': 'badge bg-danger',
        'DRIVING_LICENSE': 'badge bg-dark',
        'BIRTH_CERT': 'badge bg-light text-dark'
      };
      return classes[idType] || 'badge bg-secondary';
    },

    isExpired(date) {
      if (!date) return false;
      return new Date(date) < new Date();
    },

    isExpiringSoon(date) {
      if (!date) return false;
      const expiryDate = new Date(date);
      const today = new Date();
      const thirtyDaysFromNow = new Date(today.getTime() + (30 * 24 * 60 * 60 * 1000));
      return expiryDate > today && expiryDate <= thirtyDaysFromNow;
    },

    formatDate(date) {
      if (!date) return 'N/A';
      const d = new Date(date);
      const options = { year: 'numeric', month: 'short', day: 'numeric' };
      return d.toLocaleDateString('en-US', options);
    },

    formatFileSize(bytes) {
      if (!bytes || bytes === 0) return 'N/A';
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(1024));
      return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
    },

    viewDocument(doc) {
      if (doc.filePath) {
        window.open(uploads+''+doc.filePath, '_blank');
      } else {
        this.$toast?.info("Document file not available") ||
        alert("Document file not available");
      }
    },

    callContact(phoneNumber) {
      if (phoneNumber) {
        window.location.href = `tel:${phoneNumber}`;
      }
    },

    emailContact(email) {
      if (email) {
        window.location.href = `mailto:${email}`;
      }
    },

    editEmployee() {
      if (!this.canEditEmployees) {
        window.alert("You have view-only access to the Employee Register.");
        return;
      }
      sessionStorage.setItem("employeeId", this.employee.id);
      this.gotTo('employee-edit');
    },

    openUploadModal() {
      this.showUploadModal = true;
      this.uploadForm = { documentType: '', documentName: '', documentNumber: '', validTill: '', file: null };
      this.$nextTick(() => { if (this.$refs.uploadFileInput) this.$refs.uploadFileInput.value = ''; });
    },

    closeUploadModal() {
      this.showUploadModal = false;
      this.uploading = false;
    },

    handleUploadFileSelect(e) {
      const file = e.target.files[0];
      if (!file) return;
      if (file.size > 5 * 1024 * 1024) {
        this.$toast?.error("File must be less than 5MB") || alert("File must be less than 5MB");
        e.target.value = '';
        return;
      }
      const allowed = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png',
        'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!allowed.includes(file.type)) {
        this.$toast?.error("Only PDF, JPG, PNG, DOC, DOCX allowed") || alert("Invalid file type");
        e.target.value = '';
        return;
      }
      this.uploadForm.file = file;
    },

    async submitUploadOnBehalf() {
      if (!this.uploadForm.file || !this.employee?.nationalId) {
        this.$toast?.error("Select a file and ensure employee is loaded") || alert("Select a file");
        return;
      }
      this.uploading = true;
      try {
        const formData = new FormData();
        formData.append("function", "uploadEmployeeDocument");
        formData.append("forNationalId", this.employee.nationalId);
        formData.append("file", this.uploadForm.file);
        formData.append("documentType", this.uploadForm.documentType);
        formData.append("documentName", this.uploadForm.documentName);
        formData.append("documentNumber", this.uploadForm.documentNumber);
        formData.append("validTill", this.uploadForm.validTill);
        const res = await execute(formData, authUrl);
        if (res?.data?.success) {
          this.$toast?.success("Document uploaded successfully");
          this.closeUploadModal();
          await this.loadEmployeeDetails();
        } else {
          throw new Error(res?.data?.message || "Upload failed");
        }
      } catch (err) {
        this.$toast?.error("Upload failed: " + err.message) || alert("Upload failed: " + err.message);
      } finally {
        this.uploading = false;
      }
    },

    gotTo(route) {
      this.$router.push(route);
    }
  }
};
</script>

<style scoped>
/* Layout: content must not overlap fixed Navigation (280px / 70px collapsed) */
#layout-wrapper {
  min-height: 100vh;
  box-sizing: border-box;
}

.content-minimize {
  margin-left: 280px;
  padding: 20px;
  min-height: 100vh;
  transition: margin-left 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-sizing: border-box;
}

#layout-wrapper.nav-collapsed .content-minimize {
  margin-left: 70px;
}

.page-content {
  max-width: 1600px;
  margin: 0 auto;
}

.page-inner {
  width: 100%;
}

@media (max-width: 1024px) {
  .content-minimize {
    margin-left: 70px;
  }
}

@media (max-width: 768px) {
  .content-minimize {
    margin-left: 0;
    padding: 15px;
  }
  #layout-wrapper.nav-collapsed .content-minimize {
    margin-left: 0;
  }
}

.avatar-xl {
  width: 80px;
  height: 80px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.avatar-lg {
  width: 60px;
  height: 60px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.avatar-title {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  font-weight: 600;
}

.nav-tabs .nav-link {
  border: none;
  padding: 1rem 1.5rem;
  color: #6c757d;
  font-weight: 500;
  transition: all 0.3s ease;
}

.nav-tabs .nav-link:hover {
  border: none;
  color: #059669;
  background-color: transparent;
}

.nav-tabs .nav-link.active {
  background-color: transparent;
  border: none;
  color: #059669;
  border-bottom: 2px solid #059669;
}

.table-borderless td {
  border: none;
  padding: 0.75rem 0;
}

.card {
  transition: all 0.3s ease;
  border: 1px solid #e9ecef;
}

.card:hover {
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
}

.document-icon, .contact-icon, .kin-icon {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f8f9fa;
  border-radius: 8px;
}

.document-details, .contact-details, .kin-details {
  margin-bottom: 1rem;
}

.badge {
  font-weight: 500;
  padding: 0.5em 0.75em;
}

.btn-group-sm .btn {
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
}

.verification-status .badge {
  font-size: 0.75rem;
}

@media (max-width: 768px) {
  .nav-tabs .nav-link {
    padding: 0.5rem 1rem;
    font-size: 0.875rem;
  }

  .avatar-xl {
    width: 60px;
    height: 60px;
  }

  .btn-group .dropdown-toggle {
    padding: 0.375rem 0.75rem;
  }
}

/* Loading state */
.spinner-border {
  width: 3rem;
  height: 3rem;
}

/* Tab content spacing */
.tab-pane {
  padding-top: 1rem;
}

/* Card hover effects */
.border:hover {
  border-color: #059669 !important;
}

/* Badge styling */
.badge.bg-outline-secondary {
  background-color: transparent !important;
  color: #6c757d;
  border: 1px solid #6c757d;
}
</style>